/**
 * nosto-cart-sync — synchronise Nosto avec le panier + attribue les conversions
 * issues des surfaces de reco Nosto.
 *
 * Deux responsabilités, toutes deux branchées sur le MÊME signal : l'événement
 * `cart:update` ('cart:update') que Horizon / AxCartManager dispatchent sur
 * `document` APRÈS qu'une mutation panier a été persistée (add via product-form,
 * quick-add, qty, remove, ajout tiers via cart-manager).
 *
 *   1. Sync panier-session — `Nosto.reloadCart()` (helper officiel Shopify×Nosto,
 *      exposé sur `window.Nosto` une fois le SDK booté). Met à jour le panier dans le
 *      Debug Toolbar (Visitor → Session → Cart) et émet l'event cartupdated.
 *      Doc : https://docs.nosto.com/shopify/features/add-to-cart
 *
 *   2. Attribution de conversion — quand l'ATC vient d'une surface de reco Nosto
 *      (reco PDP, upsell sidecart, onglet ax-tabbed-product-lists piloté par Nosto),
 *      on appelle `api.recommendedProductAddedToCart(productId, placement)` pour que
 *      Nosto attribue la conversion au placement qui l'a générée. Le reloadCart
 *      ci-dessus couvre le QUOI (contenu du panier) ; cet appel couvre le OÙ (quel
 *      placement a converti).
 *
 * ── Pourquoi le bus `cart:update` (et PAS une interception fetch/XHR) ────────────
 *   Certaines bases Dawn publient leur pub/sub panier à des instants incohérents
 *   (avant/après le swap DOM selon la source), ce qui force l'interception fetch.
 *   Nutrimuscle, lui, repose sur AxCartManager : `cart:update` est dispatché sur
 *   `document` de façon fiable APRÈS persistance, quelle que soit la source. On se
 *   greffe donc directement dessus — plus léger, pas de monkey-patch de `fetch`,
 *   et cohérent avec le reste du sidecart (voir .claude/rules/sidecart.md).
 *
 * ── Détection de surface ─────────────────────────────────────────────────────────
 *   Capture (phase capture) des `click`/`submit` : si la cible est dans
 *   `closest('[data-nosto-placement]')`, on mémorise l'intention {productId,
 *   placement}. Le productId vient de `[data-nosto-product-id]` (slide reco) ou, à
 *   défaut, de `[data-product-id]` (wrapper de carte). Les 3 surfaces exposent ces
 *   attributs → un seul résolveur les couvre toutes. L'attribution est consommée au
 *   prochain `cart:update` (TTL 30 s : le variant-picker peut rester ouvert un moment
 *   entre le clic et l'ajout réel).
 *
 * Load order : déclaré dans layout/theme.liquid avec `defer`, uniquement si Nosto est
 * activé. `defer` est sûr — les listeners sont posés avant toute action panier.
 */
(function () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__nostoCartSyncInstalled) return;
  window.__nostoCartSyncInstalled = true;

  const CART_UPDATE_EVENT = 'cart:update';
  const ATTRIBUTION_TTL_MS = 30000;

  // Dernière intention d'ATC issue d'une surface de reco Nosto, ou null.
  let pendingAttribution = null;

  /**
   * Résout {placement, productId} pour un élément dans (ou égal à) une surface Nosto.
   * @param {EventTarget | null} el
   * @returns {{ productId: string, placement: string, ts: number } | null}
   */
  function resolveAttribution(el) {
    if (!el || typeof el.closest !== 'function') return null;
    const surface = el.closest('[data-nosto-placement]');
    if (!surface) return null;
    const placement = surface.getAttribute('data-nosto-placement');
    if (!placement) return null;

    let productId = null;
    const slide = el.closest('[data-nosto-product-id]');
    if (slide) productId = slide.getAttribute('data-nosto-product-id');
    if (!productId) {
      const card = el.closest('[data-product-id]');
      if (card) productId = card.getAttribute('data-product-id');
    }
    if (!productId) return null;

    return { productId: String(productId), placement, ts: Date.now() };
  }

  // ── click capture ─────────────────────────────────────────────────────────────
  // Couvre le cas où l'ATC ouvre un variant-picker portalé hors de la surface (le
  // form soumis n'est alors plus un descendant de `[data-nosto-placement]`).
  document.addEventListener(
    'click',
    (e) => {
      try {
        const attr = resolveAttribution(e.target);
        if (attr) pendingAttribution = attr;
      } catch (_) {}
    },
    true
  );

  // ── submit capture ────────────────────────────────────────────────────────────
  // ATC soumis directement dans une surface (pas d'étape modale) → productId précis.
  document.addEventListener(
    'submit',
    (e) => {
      try {
        const form = e.target;
        if (!form || form.tagName !== 'FORM') return;
        const action = form.getAttribute('action') || '';
        if (!/\/cart\/add(?:\.js)?(?:[?#]|$)/.test(action)) return;
        const attr = resolveAttribution(form);
        if (attr) pendingAttribution = attr;
      } catch (_) {}
    },
    true
  );

  // ── cart:update ─────────────────────────────────────────────────────────────────
  document.addEventListener(CART_UPDATE_EVENT, () => {
    // Attribution (optionnelle) — wrappée pour ne jamais empêcher le sync ci-dessous.
    try {
      if (pendingAttribution && Date.now() - pendingAttribution.ts < ATTRIBUTION_TTL_MS) {
        const attr = pendingAttribution;
        pendingAttribution = null;
        if (typeof window.nostojs === 'function') {
          window.nostojs((api) => {
            const fn = api.recommendedProductAddedToCart || api.reportAddToCart;
            if (typeof fn === 'function') {
              try {
                fn.call(api, attr.productId, attr.placement);
              } catch (err) {
                console.warn('[NostoCartSync] attribution failed:', err);
              }
            }
          });
        }
      } else {
        // Périmée ou ATC hors surface Nosto → on ne garde pas une intention obsolète.
        pendingAttribution = null;
      }
    } catch (err) {
      console.warn('[NostoCartSync] attribution block failed:', err);
    }

    // Sync panier-session — appel porteur, doit tourner même sans contexte placement.
    if (window.Nosto && typeof window.Nosto.reloadCart === 'function') {
      Promise.resolve(window.Nosto.reloadCart()).catch((err) => {
        console.warn('[NostoCartSync] Nosto.reloadCart failed:', err);
      });
    }
  });
})();
